/**
 * localStorage cache of REVEALED balance values (NOT signatures) — spec §7.5.
 *
 * Key tuple: (chainId, safe, token, signer). `chainId` is REQUIRED — dropping
 * it would render chain A's balance on chain B for the same Safe address.
 *
 * The value is a bigint stringified into a JSON-safe form. Entries older than
 * 5 minutes are treated as stale. No signature is stored — every reveal
 * re-signs the envelope.
 */
import { useCallback, useMemo } from 'react'
import type { Address } from 'viem'
import { BALANCE_VALUE_CACHE_TTL_MS, SRC20_BALANCE_CACHE_KEY_PREFIX } from '../constants'
import type { BalanceValueCacheEntry } from '../types'

type CacheKeyParts = { chainId: number; safe: Address; token: Address; signer: Address }

const keyFor = ({ chainId, safe, token, signer }: CacheKeyParts): string =>
  `${SRC20_BALANCE_CACHE_KEY_PREFIX}:${chainId}:${safe.toLowerCase()}:${token.toLowerCase()}:${signer.toLowerCase()}`

const safeStorage = (): Storage | undefined => {
  if (typeof window === 'undefined') return undefined
  try {
    return window.localStorage
  } catch {
    return undefined
  }
}

const readEntry = (parts: CacheKeyParts): BalanceValueCacheEntry | undefined => {
  const store = safeStorage()
  if (!store) return undefined
  try {
    const raw = store.getItem(keyFor(parts))
    if (!raw) return undefined
    const parsed = JSON.parse(raw) as Partial<BalanceValueCacheEntry>
    if (
      typeof parsed.value !== 'string' ||
      typeof parsed.revealedAt !== 'number' ||
      typeof parsed.chainId !== 'number'
    ) {
      return undefined
    }
    return parsed as BalanceValueCacheEntry
  } catch {
    return undefined
  }
}

const writeEntry = (entry: BalanceValueCacheEntry): void => {
  const store = safeStorage()
  if (!store) return
  try {
    store.setItem(
      keyFor({ chainId: entry.chainId, safe: entry.safe, token: entry.token, signer: entry.signer }),
      JSON.stringify(entry),
    )
  } catch {
    /* ignore quota errors */
  }
}

const deleteEntry = (parts: CacheKeyParts): void => {
  const store = safeStorage()
  if (!store) return
  try {
    store.removeItem(keyFor(parts))
  } catch {
    /* ignore */
  }
}

export interface BalanceValueCacheHook {
  get: (parts: CacheKeyParts) => { raw: bigint; revealedAt: number } | undefined
  set: (parts: CacheKeyParts & { raw: bigint }) => void
  invalidate: (parts: CacheKeyParts) => void
  /** Invalidate every cached value for (chainId, safe, token) — across signers. */
  invalidateToken: (chainId: number, safe: Address, token: Address) => void
}

export const useBalanceValueCache = (): BalanceValueCacheHook => {
  const get = useCallback((parts: CacheKeyParts) => {
    const entry = readEntry(parts)
    if (!entry) return undefined
    if (Date.now() - entry.revealedAt * 1000 > BALANCE_VALUE_CACHE_TTL_MS) return undefined
    let raw: bigint
    try {
      raw = BigInt(entry.value)
    } catch {
      return undefined
    }
    return { raw, revealedAt: entry.revealedAt }
  }, [])

  const set = useCallback((parts: CacheKeyParts & { raw: bigint }) => {
    writeEntry({
      chainId: parts.chainId,
      safe: parts.safe,
      token: parts.token,
      signer: parts.signer,
      value: parts.raw.toString(),
      revealedAt: Math.floor(Date.now() / 1000),
    })
  }, [])

  const invalidate = useCallback((parts: CacheKeyParts) => deleteEntry(parts), [])

  const invalidateToken = useCallback((chainId: number, safe: Address, token: Address) => {
    const store = safeStorage()
    if (!store) return
    const prefix = `${SRC20_BALANCE_CACHE_KEY_PREFIX}:${chainId}:${safe.toLowerCase()}:${token.toLowerCase()}:`
    try {
      const keysToRemove: string[] = []
      for (let i = 0; i < store.length; i++) {
        const k = store.key(i)
        if (k && k.startsWith(prefix)) keysToRemove.push(k)
      }
      for (const k of keysToRemove) store.removeItem(k)
    } catch {
      /* ignore */
    }
  }, [])

  return useMemo(() => ({ get, set, invalidate, invalidateToken }), [get, set, invalidate, invalidateToken])
}

export default useBalanceValueCache
